import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Mail } from "lucide-react";
import { api } from "../api";
import Brand from "../../components/site/Brand";

/**
 * Forgot Password — one-field form.
 * Always shows the same success message regardless of whether the email
 * exists (matches backend, which never leaks user existence).
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex items-center justify-center px-5 py-10" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Brand tone="dark" />
        </div>

        <div className="bg-white rounded-2xl shadow-[0_20px_50px_-25px_rgba(11,18,32,0.25)] border border-slate-200 p-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
            Account recovery
          </div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl tracking-tight text-slate-900 font-semibold">
            Reset your password.
          </h1>

          {sent ? (
            <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-[13.5px] text-emerald-900 leading-relaxed" data-testid="forgot-password-sent">
              <div className="flex items-center gap-2 font-semibold">
                <Mail size={14} /> Check your inbox
              </div>
              <p className="mt-1.5 text-emerald-900/85">
                If an account exists for <b>{email}</b>, we've sent a link to set a
                new password. The link expires in 60 minutes. Also check Promotions
                and Spam folders.
              </p>
              <div className="mt-4">
                <Link
                  to="/app/login"
                  data-testid="forgot-password-back-login"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] text-white px-4 py-2.5 text-sm font-semibold"
                >
                  Back to sign in <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ) : (
            <>
              <p className="mt-2 text-slate-500 text-sm">
                Enter the email address on your CanniComply account. We'll send you
                a link to set a new password.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Work email</span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-password-email"
                    placeholder="you@yourbusiness.co.uk"
                    className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
                  />
                </label>

                {error && (
                  <div data-testid="forgot-password-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="forgot-password-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-3 text-sm font-semibold"
                >
                  {busy ? (<><Loader2 size={14} className="animate-spin" /> Sending link…</>) : (<>Send reset link <ArrowRight size={14} /></>)}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Remembered it?{" "}
          <Link to="/app/login" className="text-[#8C6B33] hover:text-[#0B1220] font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
